import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Row, Col, Button, Divider, Alert, Typography, message } from "antd";
import { SafetyCertificateOutlined, BankOutlined, EnvironmentOutlined, KeyOutlined } from "@ant-design/icons";
import InrOutlined from "../common/InrOutlined";
import { registerStartupDoctorClient } from "./startupDoctorService";

const { Text } = Typography;
const { TextArea } = Input;

const INITIAL_SERVICES = ["External Compliance Service", "Private Limited Incorporation", "LLP Incorporation", "OPC", "MSME", "Proprietorship", "TDS", "PF", "Copyright", "GST Compliance", "GST Cancelation", "GST filing", "ITR", "DPIIT", "DUNS Number", "Trade Mark", "GST Registration", "ISO", "Patent", "FSSAI", "DSC", "IE code", "LEI", "BIS", "ROSH", "CH"];

const BUSINESS_STRUCTURES = [
  { label: "Proprietorship", value: "Proprietorship" },
  { label: "Limited Liability Partnership (LLP)", value: "LLP" },
  { label: "Private Limited (Pvt Ltd)", value: "Private Limited" },
  { label: "Partnership Firm", value: "Partnership" },
  { label: "Individual", value: "Individual" },
];

function StartupDoctorModal({ visible, onClose, requestData, weCrmUrl, weCrmCompanyId, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState("");

  useEffect(() => {
    if (visible && requestData) {
      const initialVals = {
        serviceName: requestData.serviceName || "",
        company_name: requestData.company_name || requestData.companyName || "",
        company_email: requestData.email || requestData.company_email || "",
        business_type: requestData.business_type || requestData.companyType || undefined,
        password: "",
        cin: requestData.cin || "",
        incorporation_date: requestData.incorporation_date || requestData.dateOfIncorporation || "",
        roc: requestData.roc || "",
        registration_number: requestData.registration_number || requestData.registrationNumber || "",
        company_origin: requestData.company_origin || "Indian",
        class_of_company: requestData.class_of_company || "Private",
        company_category: requestData.company_category || "Company limited by shares",
        company_subcategory: requestData.company_subcategory || "Non-government company",
        authorised_capital: requestData.authorised_capital || requestData.authorisedCapital || "",
        paidup_capital: requestData.paidup_capital || requestData.paidUpCapital || "",
        total_obligation_of_contribution: requestData.total_obligation_of_contribution || "",
        pan: requestData.pan || "",
        gstin: requestData.gstin || requestData.gstNumber || "",
        address_type: requestData.address_type || "Registered Address",
        main_division_no: requestData.main_division_no || "",
        postal_code: requestData.postal_code || requestData.postalCode || "",
        street_address_line_1: requestData.street_address_line_1 || requestData.streetAddressLine1 || "",
        street_address_line_2: requestData.street_address_line_2 || requestData.streetAddressLine2 || "",
        city: requestData.city || "",
        state: requestData.state || "",
        address: requestData.address || "",
      };
      form.setFieldsValue(initialVals);
      setSelectedService(initialVals.serviceName);
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, requestData?._id || requestData?.id || requestData?.email]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        ...values,
        email: values.company_email,
        owner_name: values.company_name || requestData?.owner_name || "Client",
        role: "customer",
        status: "active",
        company_id: weCrmCompanyId,
        dealvoiceLeadId: requestData?.dealvoiceLeadId || requestData?._leadId || "",
        dealvoiceCompanyCode: requestData?.dealvoiceCompanyCode || "",
        dealvoiceLeadCode: requestData?.dealvoiceLeadCode || "",
        isComplianceService: values.serviceName === "External Compliance Service" ? "true" : "false",
        directors: requestData?.directors || [],
      };

      await registerStartupDoctorClient({ weCrmUrl, payload });
      message.success("Successfully registered client in Startup Doctor!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      if (err?.errorFields) return; // Antd validation error
      console.error("Startup Doctor Registration Error:", err);
      message.error(err.message || "Failed to register client account in Startup Doctor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Text strong style={{ fontSize: "1.1rem", color: "#0f172a" }}>
          Complete Startup Doctor Onboarding
        </Text>
      }
      open={visible}
      onCancel={onClose}
      width={720}
      destroyOnClose
      style={{ top: 24 }}
      styles={{
        body: { maxHeight: "75vh", overflowY: "auto", paddingRight: 8 },
      }}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit} style={{ background: "#0f172a", borderColor: "#0f172a" }}>
          Register Client Account
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* Service Routing */}
        <Form.Item label="Initial Service (for WE-CRM bucket routing)" name="serviceName" rules={[{ required: true, message: "Please select an initial service." }]}>
          <Select placeholder="— Select service —" options={INITIAL_SERVICES.map((s) => ({ label: s, value: s }))} onChange={(val) => setSelectedService(val)} showSearch />
        </Form.Item>

        {selectedService === "External Compliance Service" && <Alert type="success" showIcon message="Compliance Radar Activated" description="Manager will upload COI on accept — incorporation date auto-generates Compliance Radar tasks (ADT-1, AOC-4, MGT-7A etc.). Min ₹15,000. No checklist." style={{ marginBottom: 16 }} />}

        {/* Company Details */}
        <Divider orientation="left" style={{ margin: "12px 0", fontSize: "0.9rem" }}>
          <BankOutlined style={{ marginRight: 6 }} /> Company Details
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Company Name" name="company_name">
              <Input placeholder="Business / Corporate name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Company Email (Login ID)"
              name="company_email"
              rules={[
                { required: true, message: "Company email is required." },
                { type: "email", message: "Please enter a valid email." },
              ]}
            >
              <Input placeholder="info@company.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Business Structure" name="business_type">
              <Select placeholder="-- Select Structure --" options={BUSINESS_STRUCTURES} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Company Account Password" name="password" rules={[{ required: true, message: "Initial password is required." }]}>
              <Input.Password prefix={<KeyOutlined />} placeholder="Set initial company password" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="CIN" name="cin">
              <Input placeholder="Corporate Identity Number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Incorporation Date" name="incorporation_date">
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="ROC" name="roc">
              <Input placeholder="e.g. ROC Vijayawada" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Registration No." name="registration_number">
              <Input placeholder="Registration Number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Company Origin" name="company_origin">
              <Input placeholder="e.g. Indian" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Class of Company" name="class_of_company">
              <Input placeholder="e.g. Private" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Company Category" name="company_category">
              <Input placeholder="e.g. Company limited by shares" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Subcategory" name="company_subcategory">
              <Input placeholder="e.g. Non-government company" />
            </Form.Item>
          </Col>
        </Row>

        {/* Financials */}
        <Divider orientation="left" style={{ margin: "12px 0", fontSize: "0.9rem" }}>
          <InrOutlined style={{ marginRight: 6 }} /> Financials
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Auth. Capital" name="authorised_capital">
              <Input placeholder="e.g. 1000000" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Paid-up Capital" name="paidup_capital">
              <Input placeholder="e.g. 100000" />
            </Form.Item>
          </Col>
        </Row>

        {/* Tax & Legal */}
        <Divider orientation="left" style={{ margin: "12px 0", fontSize: "0.9rem" }}>
          <SafetyCertificateOutlined style={{ marginRight: 6 }} /> Tax & Legal
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="PAN Number" name="pan">
              <Input placeholder="ABCDE1234F" style={{ textTransform: "uppercase" }} onChange={(e) => form.setFieldsValue({ pan: e.target.value.toUpperCase() })} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="GSTIN Number" name="gstin">
              <Input placeholder="22AAAAA1111A1Z1" style={{ textTransform: "uppercase" }} onChange={(e) => form.setFieldsValue({ gstin: e.target.value.toUpperCase() })} />
            </Form.Item>
          </Col>
        </Row>

        {/* Address Details */}
        <Divider orientation="left" style={{ margin: "12px 0", fontSize: "0.9rem" }}>
          <EnvironmentOutlined style={{ marginRight: 6 }} /> Address Details
        </Divider>

        <Form.Item label="Address Type" name="address_type">
          <Input placeholder="e.g. Registered Address" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Division No." name="main_division_no">
              <Input placeholder="Division Number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Postal Code (PIN)" name="postal_code">
              <Input placeholder="PIN Code" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Street Address Line 1" name="street_address_line_1">
              <Input placeholder="Address Line 1" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Street Address Line 2" name="street_address_line_2">
              <Input placeholder="Address Line 2" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="City" name="city">
              <Input placeholder="City" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="State" name="state">
              <Input placeholder="State" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Full Formatted Address (Legacy)" name="address">
          <TextArea rows={2} placeholder="Enter corporate physical address" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default StartupDoctorModal;
